import { internalPath } from '@sparklayer-io/dashboard/features/auth/lib/internal-path';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  Navigate,
  redirect,
} from '@tanstack/react-router';
import {
  type GuardContext,
  isStaffOnly,
  prefetchSessionEnv,
  runSessionGuards,
} from '../lib/guards';
import { loadSessionUser } from '../lib/session';
import { GUEST_ONLY_PATHS } from './auth/guest-paths';
import { HostError, RootLayout, SiteShell } from './common';

export type RouterContext = { queryClient: QueryClient };

// Pathless layout parents. Each owns the guard concerns that are about *it*, so children add no
// auth logic of their own. The session setup they all share - site/env resolution, the
// getting-ready gate, the 2FA redirect - is `runSessionGuards` in `lib/guards.ts`.

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  // The routes below catch unknown paths first; this is the backstop for anything reaching neither.
  notFoundComponent: () => <Navigate to="/" replace />,
  errorComponent: HostError,
});

/** Logged-in users bounce off guest-only pages. */
export const guestRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_guest',
  async beforeLoad({ location }) {
    const user = await loadSessionUser();
    if (!user) return;

    // Only `/login` carries a return target. A `from` pointing back at a guest-only page would
    // land straight back here, so it is never a valid destination.
    const from =
      location.pathname === '/login'
        ? internalPath((location.search as Record<string, unknown>).from)
        : undefined;
    if (from && !GUEST_ONLY_PATHS.includes(from.split(/[?#]/)[0] ?? '')) {
      throw redirect({ href: from });
    }

    throw redirect({ to: '/' });
  },
});

/**
 * Pages that need no login but are not guest-only - `/reset-password/$token`, `/register-invite`
 * and friends. A logged-out visitor walks straight in; a logged-in one still gets the session
 * setup, the getting-ready gate and the 2FA redirect.
 */
export const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_public',
  async beforeLoad({ location, params, matches }) {
    const user = await loadSessionUser();
    if (!user) return;
    await runSessionGuards({
      user,
      params: params as GuardContext['params'],
      location,
      matches: matches as GuardContext['matches'],
    });
  },
});

/**
 * Authenticated pages. `beforeLoad` runs before any child: not logged in ->
 * /login?from=..., a staff-only page without staff -> /, no team -> /no-team, then the shared
 * session setup.
 */
export const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authed',
  async beforeLoad({ location, params, matches }) {
    prefetchSessionEnv(params as GuardContext['params']);
    const user = await loadSessionUser();
    if (!user) {
      throw redirect({ to: '/login', search: { from: location.href } });
    }

    // Before the team check and before any site loads, so a non-staff visitor to another site's
    // `/staff` is turned away without the session switching to that site on the way.
    if (isStaffOnly(matches as GuardContext['matches']) && !user.user.is_spark_staff) {
      throw redirect({ to: '/' });
    }

    // `/no-team` and `/logout` are the guard's own destinations and must never bounce back.
    if (!user.currentTeam && location.pathname !== '/no-team' && location.pathname !== '/logout') {
      throw redirect({ to: '/no-team' });
    }

    await runSessionGuards({
      user,
      params: params as GuardContext['params'],
      location,
      matches: matches as GuardContext['matches'],
    });
  },
});

/**
 * Chrome for the authenticated pages that carry a `siteId` but no `siteEnv` - the partner portal,
 * `/settings/*`, `/staff/*` and `/help`. Renders the sidebar/layout shell around them the way
 * `siteRoute` does for the `$siteId/$siteEnv` pages. Pathless and guard-free: `authedRoute` has
 * already resolved the environment each of them runs against. The transitional pages that have no
 * site at all (`/no-team`, `/getting-ready`, `/switch-account`, `/logout`) stay outside it.
 */
export const authedChromeRoute = createRoute({
  getParentRoute: () => authedRoute,
  id: '_chrome',
  component: SiteShell,
});

/**
 * The site-env layout. It carries no guard of its own: `authedRoute` has already resolved the
 * site, corrected the environment and gated an unprovisioned one. `remountDeps` forces a remount
 * of the subtree when the site/env pair changes.
 */
export const siteRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '$siteId/$siteEnv',
  component: SiteShell,
  remountDeps: ({ params }) => `${params.siteId}:${params.siteEnv}`,
});
